import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";

//mui-icons
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FolderZipIcon from "@mui/icons-material/FolderZip";

//react-dropzone
import { useDropzone } from "react-dropzone";

//狀態管理
import { observer } from "mobx-react";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
import { getConstByStandard } from "../../utils/getConstFileByStandard";

//api
import { dataEntryAPI } from "../../API/dataEntryAPI";

//toast
import { toast } from "react-toastify";
import handleAPIError, {
  MESSAGE_CODES,
  showErrorToast,
  showSuccessToast,
} from "../../../services/toast";
//翻譯
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
const tSystem = (key, options) =>
  i18n.t(key, {
    ns: "System",
    ...(options && typeof options === "object" ? options : {}),
  });


//upload zip file
const ZipDropzone = observer(({ files, setFiles, oldFiles, setOldFiles }) => {
  const { t } = useTranslation("ActivityMain");
  const maxSize = 500 * 1024 * 1024; //zip檔案大小是500mb

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize,
    multiple: false, // 限制只能一個檔案
    accept: {
      "application/zip": [".zip"],
      "application/x-rar-compressed": [".rar"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      // 處理錯誤的檔案
      if (fileRejections.length > 0) {
        const invalidTypeFiles = fileRejections.filter((rejection) =>
          rejection.errors.some((err) => err.code === "file-invalid-type"),
        );
        const tooLargeFiles = fileRejections.filter((rejection) =>
          rejection.errors.some((err) => err.code === "file-too-large"),
        );

        if (invalidTypeFiles.length > 0) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: ".zip, .rar" },
            fallback: tSystem("Please upload files in .zip or .rar format."),
          });
          return;
        }

        if (tooLargeFiles.length > 0) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
            i18nOptions: { size: maxSize / (1024 * 1024) },
            fallback: tSystem(
              `This file exceeds ${maxSize / (1024 * 1024)}MB.`,
            ),
          });
          return;
        }
      }

      // 正確的情況
      const file = acceptedFiles[0];
      if (file) {
        if (oldFiles?.length > 0) {
          setOldFiles([]);
        }
        setFiles([file]);
      }
    },
  });

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    // 副檔名檢查
    const validExtensions = [".zip", ".rar"];
    const fileExtension = selectedFile.name
      .substring(selectedFile.name.lastIndexOf("."))
      .toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
        i18nOptions: { types: ".zip, .rar" },
        fallback: tSystem("Only .zip or .rar files are accepted."),
      });
      return;
    }

    // 過濾超過大小的檔案
    if (selectedFile.size > maxSize) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: maxSize / (1024 * 1024) },
        fallback: tSystem(`This file exceeds ${maxSize / (1024 * 1024)}MB.`),
      });
      return;
    }

    if (oldFiles?.length > 0) {
      setOldFiles([]);
    }

    // 正確的情況
    setFiles([selectedFile]);
    console.log("Selected file:", selectedFile);
  };

  const isMoreThanOneFile = oldFiles?.length > 0;

  return (
    <Box>
      <Typography sx={{ marginBottom: 1 }}>
        {t("Upload Supporting Document Single File Only")}
        <br />
        <span style={{ color: "grey" }}>{t("Maximum Size 500MB")}</span>
      </Typography>
      {/* 拖放區 */}{" "}
      <Tooltip
        title={
          isMoreThanOneFile
            ? t(
                "Only one compressed file can be uploaded. Please remove the old file before uploading a new one.",
              )
            : ""
        }
        disableHoverListener={!isMoreThanOneFile}
        placement="top"
      >
        <Paper
          {...getRootProps()}
          sx={{
            border: "2px dashed gray",
            borderRadius: 2,
            padding: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: isDragActive ? "lightblue" : "background.paper",
          }}
        >
          <FolderZipIcon color="warning" sx={{ fontSize: "56px" }} />
          <Typography variant="body1">
            {t(isDragActive ? "Release the file to upload" : "Choose a file")}
            (.zip or .rar)
          </Typography>

          {/* 按鈕：選擇檔案 */}

          <Button
            variant="contained"
            component="label"
            sx={{ ml: 2, mt: 1, textTransform: "none" }}
            disabled={isMoreThanOneFile}
          >
            {t("Select File")}
            <input
              type="file"
              accept=".zip,.rar"
              onChange={handleFileSelect}
              hidden
            />
          </Button>
        </Paper>{" "}
      </Tooltip>
      {/* 以前上傳的檔案 */}
      {oldFiles?.length > 0 && (
        <List sx={{ mt: 2, bgcolor: "background.paper", borderRadius: 2 }}>
          {oldFiles.map((file, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <Tooltip title={t("Delete")} arrow>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    color="error"
                    onClick={() => {
                      setOldFiles([]);
                      showSuccessToast({ scenarioCode: MESSAGE_CODES.FILE_REMOVE_PENDING_SAVE });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <InsertDriveFileIcon />
              </ListItemIcon>
              <ListItemText primary={`${file.fileName}.${file.extension}`} />
            </ListItem>
          ))}
        </List>
      )}
      {/* 本次上傳的檔案 */}
      {files.length > 0 && (
        <List sx={{ mt: 2, bgcolor: "background.paper", borderRadius: 2 }}>
          {files.map((file, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <Tooltip title={t("Delete")} arrow>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    color="error"
                    onClick={() => {
                      setFiles(files.filter((_, i) => i !== index));
                      showSuccessToast({ scenarioCode: MESSAGE_CODES.FILE_REMOVE_PENDING_SAVE });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon>
                <InsertDriveFileIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(2)} KB`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
});

//左右排列UI
const ListLeft = observer(({ files, setFiles, oldFiles, setOldFiles }) => {
  const { t } = useTranslation("ActivityMain");
  const maxSize = 3 * 1024 * 1024; //檔案大小
  const maxFiles = 5; //檔案數量
  const store = emissionViewStore;
  // const existingFilesCount =
  //   oldFiles?.length || 0;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize,
    accept: {
      "application/pdf": [".pdf"],
      // "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      // "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      // 檢查是否超過最大檔案數
      if (oldFiles.length + files.length + acceptedFiles.length > maxFiles) {
        showErrorToast({
          i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
          i18nOptions: { count: maxFiles },
          fallback: tSystem(
            `You can upload a maximum of ${maxFiles} files in total.`,
          ),
        });
        return;
      }

      // 處理被拒絕的檔案
      if (fileRejections.length > 0) {
        const rejectedFiles = fileRejections.map((file) => file.file.name);

        // 檢查是否有zip或rar檔案
        if (
          rejectedFiles.some(
            (file) => file.endsWith(".zip") || file.endsWith(".rar"),
          )
        ) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem("Zip and Rar files are not allowed."),
          });
        }
        // 檢查是否是不支援的檔案類型
        else if (
          fileRejections.some((file) =>
            file.errors.some((error) => error.code === "file-invalid-type"),
          )
        ) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem(
              "Unsupported file type. Only PDF, XLSX, DOCX and image files are allowed.",
            ),
          });
        }
        // 處理超過大小的檔案
        else {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
            i18nOptions: { size: maxSize / (1024 * 1024) },
            fallback: tSystem(
              `The selected file exceeds ${maxSize / (1024 * 1024)}MB.`,
            ),
          });
        }
      }
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
  });

  // 處理選擇單個或多個檔案
  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.some((file) => !/\.(pdf|xlsx|docx|jpg|jpeg|png|gif)$/i.test(file.name))) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
        i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
      });
      return;
    }
    // 檢查是否超過最大檔案數
    if (oldFiles.length + files.length + selectedFiles.length > maxFiles) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
        i18nOptions: { count: maxFiles },
        fallback: tSystem(
          `You can upload a maximum of ${maxFiles} files in total.`,
        ),
      });
      return;
    }

    // 過濾超過大小的檔案
    const validFiles = selectedFiles.filter((file) => file.size <= maxSize);
    const rejectedFiles = selectedFiles.filter((file) => file.size > maxSize);

    if (rejectedFiles.length > 0) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: maxSize / (1024 * 1024) },
        fallback: tSystem(
          `The selected file exceeds ${maxSize / (1024 * 1024)}MB.`,
        ),
      });
    }
    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleDeleteValidationData = (id) => {
    setOldFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
  };

  const handleDelete = (fileName) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography>
          {t("Uploaded file list") + " "}
          <span style={{ color: "grey" }}>({t("up to 5 files allowed")})</span>
        </Typography>
        {oldFiles?.length > 0 && (
          <List sx={{ mt: 2, mr: 2, bgcolor: "#f1f8e9", borderRadius: 2 }}>
            {oldFiles.map((file, index) => (
              <ListItem
                key={index}
                sx={{ py: 0.8 }} // 調整垂直 padding（py: padding-y）
                secondaryAction={
                  <>
                    <Tooltip title={t("Preview")} arrow>
                      <IconButton
                        edge="end"
                        aria-label="preview"
                        color="primary"
                        onClick={() => {
                          store._handleDataEntryPreviewModal();
                          store._handleSetDataEntryPreviewInfo(
                            file.path,
                            file.fileName,
                            file.extension,
                          );
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("Delete")} arrow>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        color="error"
                        onClick={() => {
                          handleDeleteValidationData(file.id);
                          showSuccessToast({ scenarioCode: MESSAGE_CODES.FILE_REMOVE_PENDING_SAVE });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${file.fileName}.${file.extension}`}
                  // secondary={`${(file.size / 1024).toFixed(2)} KB`} // 顯示文件大小
                />
              </ListItem>
            ))}
          </List>
        )}

        {files.length > 0 && (
          <List sx={{ bgcolor: "background.paper", borderRadius: 2 }} dense>
            {/* 新上傳的檔案 */}
            {files.map((file, index) => (
              <ListItem
                key={index}
                // sx={{ py: 0.5 }}
                secondaryAction={
                  <>
                    {/* preview */}
                    <Tooltip title={t("Preview")} arrow>
                      <IconButton
                        edge="end"
                        aria-label="preview"
                        color="primary"
                        onClick={() => {
                          const fileURL = URL.createObjectURL(file);
                          store._handleDataEntryPreviewModal();
                          store._handleSetDataEntryPreviewInfo(
                            fileURL,
                            file.name,
                            file.name.split(".").pop(), //type
                          );
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {/* Delete*/}
                    <Tooltip title={t("Delete")} arrow>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        color="error"
                        onClick={() => handleDelete(file.name)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ borderLeft: "2px solid #ccc", height: "100%" }}
      >
        <Box sx={{ m: 1 }}>
          {/* 拖放區 */}
          <Typography sx={{ marginBottom: 1 }}>
            {t("Upload Supporting Documents")}
            <br />
            <span style={{ color: "grey" }}>
              {t("Maximum Size Per File")} : 3MB
            </span>
          </Typography>
          <Paper
            {...getRootProps()}
            sx={{
              border: "2px dashed gray",
              borderRadius: 2,
              padding: 3,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: isDragActive ? "lightblue" : "background.paper",
            }}
          >
            {/* 這個 input 用來選擇單個或多個檔案 */}
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              hidden
              accept=".pdf, , .xlsx, .docx, .jpg, .png, .jpeg, .gif"
            />
            <CloudUploadIcon
              color={files.length >= maxFiles ? "disabled" : "primary"}
              sx={{ fontSize: "56px" }}
            />
            <Typography variant="body1">
              {t(
                isDragActive
                  ? "Release the files or folders to upload"
                  : "Choose a file",
              )}
              <br />
              (.xlsx, .pdf, .docx, image/*)
            </Typography>
            {/* 按鈕：選擇檔案 */}
            <Button
              variant="contained"
              component="label"
              disabled={files.length >= maxFiles}
              sx={{ mt: 1, textTransform: "none" }}
            >
              {t("Select File")}
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                hidden
                accept=".pdf, , .xlsx, .docx, .jpg, .png, .jpeg, .gif"
              />
            </Button>
          </Paper>
          {files.length > maxFiles && (
            <Typography
              sx={{
                color: "red",
                fontSize: "14px",
                fontWeight: "bold",
                mt: 2,
              }}
            >
              {t("You have already selected 5 files.")}
            </Typography>
          )}
        </Box>
      </Grid>
    </>
  );
});

//main
const DataEntryUploadFilesModal = observer(() => {
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const { t } = useTranslation("ActivityMain"); //之後要改
  const [selectedOption, setSelectedOption] = useState(1); // 預設為多個檔案上傳

  const scope = CONST.category[store.selectedTab]?.split("_")[0];
  const cId = store.selectedDataEntry?.companyId;

  // 更新核取按鈕選中的值
  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  //檢查按鈕是否可以點擊
  const [isSubmitting, setIsSubmitting] = useState(false);

  //這次要上傳的檔案
  const [files, setFiles] = useState([]);
  //之前上傳過的檔案
  const [oldFiles, setOldFiles] = useState([]);
  // 跟踪原始 oldFiles是否有改變
  const [originalOldFiles, setOriginalOldFiles] = useState([]);

  // 檢查是否可以選取核取按鈕
  const [disableMulti, setDisableMulti] = useState(false);
  const [disableZip, setDisableZip] = useState(false);

  //重新取的佐證資料
  const getValidationFileList = async (dataEntryId) => {
    store._handleSetValidationFileListLoading(true);
    try {
      // 呼叫 API 並直接取得處理好的陣列
      const dataArray = await dataEntryAPI.ValidationFileAPI(
        dataEntryId,
        scope,
      );
      console.log("拿到的佐證資料列表是：", dataArray);

      // 存入 MobX store
      store._handleSetValidationFileListData((prev) => {
        const newState = {
          ...prev,
          [dataEntryId]: dataArray,
        };
        return newState;
      });
    } catch (error) {
      console.error("Error fetching validation file list", error);
      store._handleSetValidationFileListData((prev) => ({
        ...prev,
        [dataEntryId]: [],
      }));
    } finally {
      store._handleSetValidationFileListLoading(false);
    }
  };

  //一載入modal就帶入數值，如果沒有過去資料的話就可以選擇核取選項
  useEffect(() => {
    if (store.showDataEntryUploadFilesModal) {
      setFiles([]);

      const currentList = store.selectedDataEntryItem?.validationFiles || [];

      setOldFiles(currentList);
      // 保存原始的 oldFiles 狀態
      setOriginalOldFiles(currentList);

      if (currentList?.length > 0) {
        const firstExtension = currentList[0].extension?.toLowerCase();
        const isArchiveFile =
          firstExtension === "zip" || firstExtension === "rar";

        setSelectedOption(isArchiveFile ? 2 : 1);
        setDisableMulti(isArchiveFile);
        setDisableZip(!isArchiveFile);
      } else {
        setSelectedOption(1);
        setDisableZip(false);
        setDisableMulti(false);
      }
    }
  }, [store.showDataEntryUploadFilesModal]);

  // // 用於釋放為預覽創建的臨時 URL 資源
  useEffect(() => {
    // 返回一個清理函數，在組件卸載或 dependencies 變化時執行
    return () => {
      // 當模態框關閉時，釋放所有為 files 數組中的文件創建的臨時 URL
      files.forEach((file) => {
        if (file.previewURL) {
          // 假設我們在創建 URL 時將其保存在 file 對象的 previewURL 屬性上
          URL.revokeObjectURL(file.previewURL);
          console.log(`Released temporary URL for: ${file.name}`);
        }
      });
    };
  }, [files]); // 依賴於 files，當 files 變化時會重新設置清理函數

  if (
    !emissionViewStore.selectedDataEntry ||
    !emissionViewStore.selectedDataEntryItem
  )
    return null;

  const uploadDataEntry = async (
    dataEntryId,
    companyId,
    modeId, //多個檔案=1，zip=2
    filesId, // 如果該筆 dataEntryId 以前有上傳過檔案的話，要將檔案的 id 貼上
    files, // 檔案物件（File 或 File[]）
    scope,
  ) => {
    store._handleSetValidationFileListLoading(true);
    try {
      console.log("Upload parameters:", {
        dataEntryId,
        companyId,
        modeId,
        filesId,
        filesCount: files.length,
        fileNames: files.map((f) => f.name),
        scope,
      });
      return await dataEntryAPI.SetValidationFileAPI(
        dataEntryId,
        companyId,
        modeId, //多個檔案=1，zip=2
        filesId, // 如果該筆 dataEntryId 以前有上傳過檔案的話，要將檔案的 id 貼上
        files, // 檔案物件（File 或 File[]）
        scope,
      );
    } catch (error) {
      console.error("Error data setting", error);
      throw error;
    } finally {
      store._handleSetValidationFileListLoading(false);
    }
  };

  const handleSave = async () => {
    // 如果正在提交中，直接返回
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      console.log("Save operation starting with:", {
        originalOldFiles: originalOldFiles,
        currentOldFiles: oldFiles,
        newFiles: files,
      });

      // 構建 filesId
      const filesId =
        oldFiles.length > 0 ? oldFiles.map((file) => file.id).join(",") : "";

      // 情況1：舊檔案被刪除且有新檔案上傳
      if (
        originalOldFiles.length > 0 &&
        oldFiles.length === 0 &&
        files.length > 0
      ) {
        console.log("Case: Old files deleted and new files added");

        // 這裡我們需要告訴後端同時刪除舊檔案並上傳新檔案
        // 可能需要添加一個特殊的標記或調用專門的 API

        // 方案1：如果API支持，可直接傳入新檔案而不帶 filesId
        const uploadResponse = await uploadDataEntry(
          store.selectedDataEntryItem.id,
          cId,
          selectedOption,
          "", // 空字串表示刪除舊檔案
          files, // 上傳新檔案
          scope,
        );

        if (uploadResponse) {
          await getValidationFileList(store.selectedDataEntryItem.id);
          showSuccessToast({
            apiData: uploadResponse,
            scenarioCode: MESSAGE_CODES.FILE_UPLOAD_SUCCESS,
          });
          store._handleCloseDataEntryUploadFilesModal();
        } else {
          handleAPIError(null, { throwError: false });
        }
        setIsSubmitting(false);
        return;
      }

      // 情況2：僅刪除舊檔案，沒有新檔案
      if (
        originalOldFiles.length > 0 &&
        oldFiles.length === 0 &&
        (!files || files.length === 0)
      ) {
        console.log("Case: Only deleting old files");
        const uploadResponse = await uploadDataEntry(
          store.selectedDataEntryItem.id,
          cId,
          selectedOption,
          "", // 空字串表示沒有檔案
          [], // 空陣列表示沒有新檔案
          scope,
        );

        if (uploadResponse) {
          await getValidationFileList(store.selectedDataEntryItem.id);
          showSuccessToast({
            apiData: uploadResponse,
            scenarioCode: MESSAGE_CODES.FILE_DELETE_SUCCESS,
          });
          store._handleCloseDataEntryUploadFilesModal();
        } else {
          handleAPIError(null, { throwError: false });
        }
        setIsSubmitting(false);
        return;
      }

      // 情況3：沒有變更舊檔案，只上傳新檔案
      // 情況4：沒有任何變更
      if (!files || files.length === 0) {
        // 如果沒有新檔案，且目前檔案列表也為空
        if (oldFiles.length === 0) {
          toast.success(tSystem("Updated, and no new files were uploaded this time."),
          );
          setIsSubmitting(false);
          store._handleCloseDataEntryUploadFilesModal();
          return;
        }
      }

      const uploadResponse = await uploadDataEntry(
        store.selectedDataEntryItem.id,
        cId,
        selectedOption,
        filesId,
        files,
        scope,
      );

      if (uploadResponse) {
        await getValidationFileList(store.selectedDataEntryItem.id);
        showSuccessToast({
          apiData: uploadResponse,
          scenarioCode: MESSAGE_CODES.FILE_UPLOAD_SUCCESS,
        });
        store._handleCloseDataEntryUploadFilesModal();
      } else {
        handleAPIError(null, { throwError: false });
      }
    } catch (error) {
      // 錯誤處理部分不變
      console.error("Detailed error in upload operation", error);
      if (error.response) {
        console.error("Server response error:", error.response.data);
        handleAPIError(error, { throwError: false });
      } else if (error.request) {
        console.error("No response received:", error.request);
        handleAPIError(error, {
          scenarioCode: MESSAGE_CODES.FILE_NETWORK_ERROR,
          throwError: false
        });
      } else {
        console.error("Error setting up request:", error.message);
        handleAPIError(error, { throwError: false });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog
      open={store.showDataEntryUploadFilesModal}
      onClose={() => store._handleDataEntryUploadFilesModal()}
      maxWidth={false}
      PaperProps={{
        sx: {
          height: "550px",
          width: selectedOption == 2 ? "500px" : "800px",
        },
      }}
    >
      <DialogTitle>
        <IconButton
          onClick={() => store._handleCloseDataEntryUploadFilesModal()}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {t("Upload Validation File For") +
          " " +
          store.selectedDataEntry.itemName}
      </DialogTitle>
      {/* <Divider /> */}
      <FormControl sx={{ px: 3 }}>
        <RadioGroup row value={selectedOption} onChange={handleChange}>
          <FormControlLabel
            value={1}
            control={<Radio />}
            label={t("Upload multiple files")}
            sx={{ marginRight: 15 }}
            disabled={disableMulti}
          />
          <FormControlLabel
            value={2}
            control={<Radio />}
            label={t("Upload zip file")}
            disabled={disableZip}
          />
        </RadioGroup>
      </FormControl>
      <DialogContent sx={{ overflow: "hidden" }}>
        {/* zip upload */}
        {selectedOption == 2 ? (
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Grid size={12}>
              <ZipDropzone
                files={files}
                setFiles={setFiles}
                oldFiles={oldFiles}
                setOldFiles={setOldFiles}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="space-around"
            alignItems="flex-start"
            height="100%"
          >
            <ListLeft
              files={files}
              setFiles={setFiles}
              oldFiles={oldFiles}
              setOldFiles={setOldFiles}
            />
          </Grid>
        )}
        {/* 

        {/* 核取按鈕 */}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => store._handleCloseDataEntryUploadFilesModal()}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSubmitting}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? t("Save...") : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DataEntryUploadFilesModal;
